/**
 * Display names for the strategic UI (M4 plan I6): wares, factions, station templates, recipes and hulls, read
 * from the strategic data files. Unknown ids fall back to the id itself, so a newer core never breaks a screen.
 * Lookup tables are built once at module load.
 */
import factionsJson from '../../data/strategic/factions.json'
import modulesJson from '../../data/strategic/modules.json'
import templatesJson from '../../data/strategic/templates.json'
import waresJson from '../../data/strategic/wares.json'

function table(entries: readonly (readonly [string, string])[]): ReadonlyMap<string, string> {
  return new Map(entries)
}

const WARE_NAMES = table(waresJson.wares.map((w) => [w.id, w.name] as const))
const FACTION_NAMES = table(factionsJson.factions.map((f) => [f.id, f.name] as const))
const TEMPLATE_NAMES = table(templatesJson.templates.map((t) => [t.id, t.noun] as const))

/** Recipe id → the names of what it makes ("refinery.steel" → "Steel"). */
const RECIPE_NAMES: ReadonlyMap<string, string> = (() => {
  const map = new Map<string, string>()
  for (const m of modulesJson.modules) {
    const recipes: readonly { id: string; outputs?: Partial<Record<string, number>> }[] = 'recipes' in m && Array.isArray(m.recipes) ? m.recipes : []
    for (const r of recipes) {
      const outputs = r.outputs ?? {}
      const outs = Object.keys(outputs)
        .filter((w) => outputs[w] !== undefined)
        .map((w) => WARE_NAMES.get(w) ?? w)
      map.set(r.id, outs.length > 0 ? outs.join(', ') : r.id)
    }
  }
  return map
})()

/** "Water", or the id. */
export function wareName(id: string): string {
  return WARE_NAMES.get(id) ?? id
}

/** "Reach Charter Authority", or the id. */
export function factionName(id: string): string {
  return FACTION_NAMES.get(id) ?? id
}

/** "Charter Hub", or the id. */
export function templateName(id: string): string {
  return TEMPLATE_NAMES.get(id) ?? id
}

/** What a recipe makes ("Steel"), or the recipe id. */
export function recipeName(id: string): string {
  return RECIPE_NAMES.get(id) ?? id
}

/** A hull id as a label ("hauler" → "Hauler"). */
export function hullName(id: string): string {
  return id.length === 0 ? id : id[0].toUpperCase() + id.slice(1).replace(/_/g, ' ')
}

/** The systems of a galaxy (only what the name lookup reads). */
export interface NamedSystems {
  readonly systems: readonly { readonly id: string; readonly name: string }[]
}

/** One id → name table per galaxy, built on first use. */
const SYSTEM_TABLES = new WeakMap<NamedSystems, ReadonlyMap<string, string>>()

/**
 * A system's display name ("sys-05" → "Vaenvyl"), or null when the galaxy is missing or does not know the id. Unlike
 * the lookups above it never falls back to the id: no raw id reaches player text (H6, D-TBD-17).
 */
export function systemNameIn(galaxy: NamedSystems | null | undefined, id: string): string | null {
  if (galaxy === null || galaxy === undefined) return null
  let names = SYSTEM_TABLES.get(galaxy)
  if (names === undefined) {
    names = table(galaxy.systems.map((s) => [s.id, s.name] as const))
    SYSTEM_TABLES.set(galaxy, names)
  }
  return names.get(id) ?? null
}
